import { FC, useEffect, useMemo, useRef, useState } from 'react'
import { Empty } from 'antd'
import classNames from 'classnames'
import dayjs from '@store/timetable/dayjs.config'
import Modal from '@common/Modal'
import Button from '@UI/New/Button'
import { IcoRemove } from '@/utils/icons'
import { useAddAlert } from '@/hooks/useAddAlert'
import mclasses from '@pages/Admin/CRM/styles/modal.module.css'
import { IGroupLessonPaymentByLessonItem } from '../../../../utils/types'
import {
  getStatusColor,
  getStatusDescription,
  LESSON_STATUS_OPTIONS,
} from '../../../../utils/lessonStatus'
import {
  getStudentsLessonStatuses,
  IStudentLessonStatus,
  updateStudentsLessonStatuses,
} from './utils/api'
import cn from './index.module.css'
import FieldWrapper from '@pages/Admin/CourseSettings/components/FieldWrapper/FieldWrapper'
import UserCard from '@pages/Admin/Manage/tabs/ClassesPage/components/ShowUsersModal/components/UserCard/UserCard'
import Preloader from '@UI/Preloader'
import SelectField from '@pages/Admin/CourseSettings/components/SelectField/SelectField'
import TextAreaField from '@pages/Admin/CourseSettings/components/TextAreaField/TextAreaField'

interface IChangeStudentsLessonStatusModal {
  open: boolean
  lesson: IGroupLessonPaymentByLessonItem | null
  onDismiss: () => void
  onSuccess?: () => void
}

const formatLessonDate = (date?: string | null) => {
  if (!date) return '-'

  const value = dayjs(date)

  return value.isValid() ? value.format('DD.MM.YYYY HH:mm') : '-'
}

const ChangeStudentsLessonStatusModal: FC<IChangeStudentsLessonStatusModal> = (
  props,
) => {
  return (
    <Modal
      active={props.open}
      className={classNames(mclasses.modalWrapper, cn.modalWrapper) as any}
      setActive={() => props.onDismiss()}
    >
      <Body {...props} />
    </Modal>
  )
}

const Body: FC<IChangeStudentsLessonStatusModal> = ({
  lesson,
  open,
  onDismiss,
  onSuccess,
}) => {
  const addAlert = useAddAlert()
  const [loading, setLoading] = useState(false)
  const [submitting, setSubmitting] = useState(false)
  const [rows, setRows] = useState<IStudentLessonStatus[]>([])
  const [initialRows, setInitialRows] = useState<IStudentLessonStatus[]>([])

  const loadStudents = async () => {
    if (!lesson?.id || loading) return
    setLoading(true)
    const response = await getStudentsLessonStatuses(Number(lesson?.id))

    setLoading(false)

    if (!response.success) {
      addAlert({
        title: 'Ошибка',
        text: response.error || 'Не удалось загрузить учеников занятия',
        type: 'error',
      })
      return
    }

    setRows(response.data ?? [])
    setInitialRows(response.data ?? [])
  }

  useEffect(() => {
    if (!open) return

    setRows([])
    setInitialRows([])

    if (!lesson?.id) {
      addAlert({
        title: 'Ошибка',
        text: 'Не удалось определить занятие',
        type: 'error',
      })
      return
    }

    loadStudents()
  }, [lesson?.id, open])

  const initialRowsByStudent = useMemo(() => {
    return new Map(initialRows.map((row) => [row.student.id, row]))
  }, [initialRows])

  const changedRows = useMemo(() => {
    return rows.filter((row) => {
      const initialRow = initialRowsByStudent.get(row.student.id)

      if (!initialRow) return true

      return (
        String(initialRow.status) !== String(row.status) ||
        String(initialRow.comment ?? '') !== String(row.comment ?? '')
      )
    })
  }, [initialRowsByStudent, rows])

  const updateRow = (
    studentId: number,
    field: 'status' | 'comment',
    value: string,
  ) => {
    setRows((prev) =>
      prev.map((row) =>
        row.student.id === studentId ? { ...row, [field]: value } : row,
      ),
    )
  }

  const onSave = async () => {
    if (!lesson?.id) {
      addAlert({
        title: 'Ошибка',
        text: 'Не удалось определить занятие',
        type: 'error',
      })
      return
    }

    if (!changedRows.length) {
      addAlert({ title: 'Нет изменений для сохранения' })
      return
    }

    setSubmitting(true)

    const response = await updateStudentsLessonStatuses(
      Number(lesson.id),
      changedRows.map((row) => ({
        id: row.id,
        student: row.student,
        // Без статуса уходит пустая строка, а не «null»: бэкенд читал её как «Н» и снимал списание.
        status: row.status ?? '',
        comment: row.comment ?? '',
      })),
    )

    setSubmitting(false)

    if (!response.success) {
      addAlert({
        title: 'Ошибка',
        text: response.error || 'Не удалось сохранить статусы учеников',
        type: 'error',
      })
      return
    }

    addAlert({ title: 'Статусы учеников сохранены' })
    onSuccess?.()
    onDismiss()
  }

  return (
    <div className={mclasses.modalContainer}>
      <div className={mclasses.modalHeader}>
        <div className={mclasses.modalTitleContainer}>
          <span className={mclasses.modalHeaderTitle}>
            Изменить статусы учеников занятия
          </span>
        </div>
        <div className={mclasses.closeIcon} onClick={onDismiss}>
          <IcoRemove />
        </div>
      </div>
      <div className={classNames(mclasses.formWrapper, cn.form)}>
        <div className={cn.lessonInfo}>
          <FieldWrapper label={'Курс'} className={cn.lessonInfoItem}>
            <span className={cn.lessonInfoValue}>
              {lesson?.course?.name ?? '-'}
            </span>
          </FieldWrapper>
          <FieldWrapper label={'Группа'} className={cn.lessonInfoItem}>
            <span className={cn.lessonInfoValue}>
              {lesson?.group?.name ?? '-'}
            </span>
          </FieldWrapper>
          <FieldWrapper label={'Дата и время'} className={cn.lessonInfoItem}>
            <span className={cn.lessonInfoValue}>
              {formatLessonDate(lesson?.date_time)}
            </span>
          </FieldWrapper>
        </div>
        {loading ? (
          <div className={cn.loading}>
            <Preloader />
          </div>
        ) : rows.length ? (
          <div className={cn.table}>
            <div className={classNames(cn.row, cn.headerRow)}>
              <span>Ученик</span>
              <span>Статус</span>
              <span>Комментарий</span>
            </div>
            <div className={cn.rows}>
              {rows.map((row) => (
                <div className={cn.row} key={row.id}>
                  <div className={cn.studentCell}>
                    <UserCard user={row.student} />
                    {row.joined_after_lesson && (
                      <span className={cn.joinedAfterLesson}>
                        Добавлен в группу после занятия
                      </span>
                    )}
                  </div>
                  <div className={cn.statusCell}>
                    <span
                      className={cn.statusMarker}
                      style={{ backgroundColor: getStatusColor(row.status ?? '') }}
                      title={getStatusDescription(row.status ?? '')}
                    />
                    <SelectField
                      className={cn.statusSelect}
                      value={row.status}
                      placeholder="Не отмечен"
                      options={LESSON_STATUS_OPTIONS}
                      onChange={(value) =>
                        updateRow(
                          row.student.id,
                          'status',
                          String(value ?? '0'),
                        )
                      }
                    />
                  </div>
                  <TextAreaField
                    className={cn.comment}
                    value={row.comment}
                    placeholder="Комментарий"
                    autoSize={{ minRows: 1, maxRows: 3 }}
                    onChange={(event) =>
                      updateRow(row.student.id, 'comment', event.target.value)
                    }
                  />
                </div>
              ))}
            </div>
          </div>
        ) : (
          <Empty
            image={Empty.PRESENTED_IMAGE_SIMPLE}
            description="Ученики не найдены"
          />
        )}
      </div>
      <div className={mclasses.actions}>
        <Button color="gray" onClick={onDismiss}>
          Отменить
        </Button>
        <Button
          color="primary"
          onClick={onSave}
          isLoading={submitting}
          disabled={loading || submitting || changedRows.length === 0}
        >
          Сохранить
        </Button>
      </div>
    </div>
  )
}

export default ChangeStudentsLessonStatusModal
